import React from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import Button from "components/Common/Button";
import InputSearch from "components/Common/InputSearch";
import Tag from "components/Common/Tag";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Immutable from "immutable";
import classNames from "classnames";

import { saveResponse as saveResponseAction, createTag } from "actions";
import { createAlert } from "actions/alerts";
import { ResponseRecord } from "reducers/responses/reducer";
import { fetchMany } from "actions/resources";
import { getResource, getResources } from "selectors/resources";
import { Client, Language } from "resourceModels";
import colors from "stylesheets/utilities/colors.scss";
import CheckboxPanel from "components/Common/CheckboxPanel";
import DebouncedInput from "components/Common/DebouncedInput";
import LanguageButton from "components/Common/LanguageButton";
import InfoTooltip from "components/Common/InfoTooltip";
import { findAndReorderTopTag, isTopTag } from "services/tags";
import MultiLangAnswerLabels from "./MultiLangAnswerLabels";
import "./style.scss";

const tooltipReviewableImgPath = "https://static.ada.support/tooltips/Reviewable-Answer-Tooltip-Rounded.gif";
const BLOCK_NAME = "ModalResponseDetails";

export class ModalResponseDetails extends React.Component {
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);
    this.state = {
      scrolledToBottom: false
    };

    this.handleUpdateTitle = this.handleUpdateTitle.bind(this);
    this.handleUpdateDescription = this.handleUpdateDescription.bind(this);
    this.handleUpdateButtonLabel = this.handleUpdateButtonLabel.bind(this);
    this.handleDeleteButtonLabel = this.handleDeleteButtonLabel.bind(this);
    this.handleUpdateReviewable = this.handleUpdateReviewable.bind(this);
    this.handleUpdateClarifications = this.handleUpdateClarifications.bind(
      this
    );
    this.handleSaveResponse = this.handleSaveResponse.bind(this);
    this.handleRemoveTag = this.handleRemoveTag.bind(this);
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    const { languages, fetchLanguages } = this.props;

    if (!languages) {
      fetchLanguages();
    }
  }

  /**
   * getAvailableLanguages
   * @returns {List} languages
   */
  getAvailableLanguages() {
    const {
      /** @type Client */
      client,
      languages,
      response
    } = this.props;

    return languages
      .filter(
        language => (
          client.translatedLanguages.includes(language.code) &&
          !response
            .get("buttonLabel")
            .keySeq()
            .toArray()
            .includes(language.code)
        )
      )
      .sort((a, b) => {
        if (a.englishName < b.englishName) {
          return -1;
        }

        if (a.englishName > b.englishName) {
          return 1;
        }

        return 0;
      });
  }

  /**
   * handleUpdateTitle
   * @param {Object} handle
   */
  handleUpdateTitle(handle) {
    this.props.handleUpdateResponse({ handle });
  }

  /**
   * handleUpdateDescription
   * @param {Object} description
   */
  handleUpdateDescription(description) {
    this.props.handleUpdateResponse({ description });
  }

  /**
   * handleUpdateButtonLabel
   * @param {String} buttonLabel
   * @param {String} languageCode
   */
  handleUpdateButtonLabel(buttonLabel, languageCode) {
    this.props.handleUpdateResponse({
      buttonLabel: Object.assign(
        this.props.response.get("buttonLabel").toJS(),
        { [languageCode]: buttonLabel }
      )
    });
  }

  /**
   * handleDeleteButtonLabel
   * @param {String} languageCode
   */
  handleDeleteButtonLabel(languageCode) {
    this.props.deleteButtonLabelLanguage(
      languageCode,
      this.props.response.get("id")
    );
  }

  /**
   * handleUpdateReviewable
   */
  handleUpdateReviewable() {
    this.props.handleUpdateResponse({
      reviewable: !this.props.response.get("reviewable")
    });
  }

  /**
   * handleUpdateClarifications
   */
  handleUpdateClarifications() {
    this.props.handleUpdateResponse({
      clarifications: !this.props.response.get("clarifications")
    });
  }

  /**
   * handleSaveResponse
   */
  handleSaveResponse() {
    const {
      response, saveResponse, expressionsToSave, logBodyToTrain, onSave
    } = this.props;

    if (onSave) {
      onSave(response);
    } else {
      saveResponse(
        response,
        Immutable.List(expressionsToSave),
        logBodyToTrain
      );
    }
  }

  /**
   * handleRemoveTag
   * @param {String} tagId
   */
  handleRemoveTag(tagId) {
    this.props.handleRemoveTag(tagId);
  }

  /**
   * addTag
   * @param {String} query
   */
  addTag(query) {
    const { tags } = this.props;

    if (!query) {
      return;
    }

    let tag = tags.find(t => t.name.toLowerCase() === query);

    if (isTopTag(query)) {
      tag = tags.find(t => t.name.toLowerCase() === query.toLowerCase());
    }

    if (tag) {
      if (this.props.response.tags.includes(tag.id)) {
        this.props.createAlert({
          message: `"${this.props.response.handle}" already contains this tag.`
        });
      } else {
        this.props.handleAddTag(tag.id);
      }
    } else {
      this.props.createTag(this.props.response, query);
    }
  }

  /**
   * addTag
   * @param {Object} e
   */
  handleScroll(e) {
    if (
      e.target.scrollTop >=
      e.target.scrollHeight - e.target.offsetHeight - 41
    ) {
      this.setState({
        scrolledToBottom: true
      });
    } else {
      this.setState({
        scrolledToBottom: false
      });
    }
  }

  /**
 * handlePrioritizeToptag
 * @param {Object} responseTags
 */
  handlePrioritizeTopTag(responseTags) {
    const { tags } = this.props;

    if ((tags.findIndex(tag => (isTopTag(tag.name))) !== -1) && (responseTags.size > 1)) {
      if (isTopTag(responseTags.get(0).name)) {
        return responseTags;
      }

      const topTagIndex = responseTags.findIndex(tag => (isTopTag(tag.name)));

      if (topTagIndex !== -1) {
        return findAndReorderTopTag(topTagIndex, responseTags);
      }
    }

    return responseTags;
  }

  /**
   * renderAllTags
   * @param {Object} filteredTags
   */
  renderAllTags(filteredTags) {
    const arrangedTags = this.handlePrioritizeTopTag(filteredTags);

    return (
      arrangedTags.map(tag => (
        // Not going to have a keyboard handler
        <div // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus, max-len
          role="button"
          key={tag.id}
          className={`${BLOCK_NAME}__tag-container__all-tags__tag`}
          onClick={() => this.props.handleAddTag(tag.id)}
        >
          <Tag tag={tag} hasIcon={isTopTag(tag.name)} />
          {isTopTag(tag.name) && (
            <p className={`${BLOCK_NAME}__tag-container__all-tags__tag__toggle-description`}>
              Select this ‘Top’ tag if this answer is at the start of a decision tree.
            </p>
          )}
        </div>
      ))
    );
  }

  /**
   * renderResponseTags
   */
  renderResponseTags() {
    const { tags, response } = this.props;

    const responseTags = tags.filter(tag => response.tags.includes(tag.id));
    const arrangedTags = this.handlePrioritizeTopTag(responseTags);

    return (
      arrangedTags.map(tag => (
        <div
          className={`${BLOCK_NAME}__tag-container__tags__tag`}
          key={tag.id}
        >
          <Tag tag={tag} removeTag={this.handleRemoveTag} hasIcon={isTopTag(tag.name)} />
        </div>
      ))
    );
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const maxNumberOfTags = 6;
    const {
      /** @type Client */
      client,
      /** @type ResponseRecord */
      response,
      tags,
      modalResponseDetails,
      languages,
      addButtonLabelLanguage
    } = this.props;

    // if the response is reserved, hide the "Reviewable" and "Suggest Automatically" toggles
    const shouldShowToggles = !response.get("reserved");

    if (!languages) {
      return null;
    }

    const query = modalResponseDetails.get("query");
    const filteredTags = tags.filter(
      tag => (
        tag.name.toLowerCase().indexOf(query.toLowerCase()) >= 0 &&
        !response.tags.includes(tag.id)
      )
    );

    const sortedButtonLabels = response
      .get("buttonLabel")
      .keySeq()
      .toArray()
      .sort((a, b) => {
        if (a === client.language) {
          return -1;
        }

        if (b === client.language) {
          return 1;
        }

        return 0;
      })
      .map(languageCode => [
        languageCode,
        response.getIn(["buttonLabel", languageCode])
      ]);

    const languageButton = (
      <LanguageButton
        responseId={response.get("id")}
        availableLanguages={this.getAvailableLanguages()}
        languages={languages}
        addLanguage={languageCode => addButtonLabelLanguage(languageCode, response.id)}
        color="white"
        inModal
        absolute
      />
    );
    const zendeskClient = client.features.get("zendeskClient");

    const allTagsWrapper = (
      <div className={`${BLOCK_NAME}__tag-container__all-tags-wrapper`}>
        {Boolean(filteredTags.size) && (
          <div>
            <div
              className={`${BLOCK_NAME}__tag-container__all-tags`}
              onScroll={this.handleScroll.bind(this)}
            >
              {this.renderAllTags(filteredTags)}
            </div>
            <div
              className={classNames(
                `${BLOCK_NAME}__tag-container__all-tags-wrapper__gradient`,
                {
                  [`${BLOCK_NAME}__tag-container__all-tags-wrapper__gradient--show-gradient`]:
                    filteredTags.size > maxNumberOfTags &&
                    !this.state.scrolledToBottom
                }
              )}
            />
          </div>
        )}
      </div>
    );

    const reviewableTooltipMessage = "Answer will appear with thumbs up and down buttons";

    const hasPredictiveSuggestions = client.features.get("predictiveSuggestions");

    const suggestionsTitleWithPS = "Include in Clarifications and Suggestions";
    const suggestionsCopyWithPS = "Allow the bot to serve this answer automatically to clarify questions or suggest it while the chatter types. Only answers with training will be served.";

    const suggestionsTitleWithoutPS = "Include in Clarifications";
    const suggestionsCopyWithoutPS = "Allow the bot to serve this answer automatically to clarify questions. Only answers with training will be served.";

    const suggestionsTitle = hasPredictiveSuggestions
      ? suggestionsTitleWithPS : suggestionsTitleWithoutPS;
    const suggestionCopy = hasPredictiveSuggestions
      ? suggestionsCopyWithPS : suggestionsCopyWithoutPS;

    return (
      <div
        className={classNames(`${BLOCK_NAME}`, "Modal__modal", `${BLOCK_NAME}--reviewable`)}
      >
        <h5 className="Modal__title">
          <div className="ModalResponseDetails__title-content">
            {response.handle && `'${response.handle}'`} Settings
          </div>
        </h5>
        <div className="Modal__content ModalResponseDetails__content--no-padding">
          <div className="ModalResponseDetails__input g-form__block">
            <div className={`${BLOCK_NAME}__title-info-wrapper`}>
              <label
                className={`${BLOCK_NAME}__input__label g-input__label`}
                htmlFor={`${BLOCK_NAME}__input__field__handle`}
              >
                Title
              </label>
              {response.get("reserved") && (
                <InfoTooltip
                  id="lockedTooltip"
                  blurb="Titles for locked answers cannot be changed"
                  iconDefault="Lock"
                  iconClicked="Lock"
                  absolute
                  inModal
                />
              )}
            </div>
            <DebouncedInput
              id={`${BLOCK_NAME}__input__field__handle`}
              customClassName={`
                ${BLOCK_NAME}__content__input-container__input
                                ${
      response.handle.length
        ? ""
        : `${BLOCK_NAME}__content__input-container__input g-input--invalid`
      }
                            `}
              placeholder="Answer Title"
              value={response.handle}
              onChange={this.handleUpdateTitle}
              ref={(titleInput) => {
                this.titleInput = titleInput;
              }}
              disabled={response.get("reserved")}
              autoFocus
            />
          </div>
          <div className="ModalResponseDetails__input">
            <label
              className={`${BLOCK_NAME}__input__label g-input__label`}
              htmlFor={`${BLOCK_NAME}__input__field__description`}
            >
              Description
            </label>
            <DebouncedInput
              id={`${BLOCK_NAME}__input__field__description`}
              customClassName={`${BLOCK_NAME}__content__input-container__input`}
              placeholder="Something descriptive about this answer to help your team find it in lists"
              value={response.description}
              onChange={this.handleUpdateDescription}
              isTextArea
            />
          </div>
          {(
            response.hasButtonLabel &&
            !zendeskClient
          ) && (
            <>
              <div className={`${BLOCK_NAME}__divider`} />
              <div className={`${BLOCK_NAME}__row-wrapper`}>
                <div className={`${BLOCK_NAME}__quick-answer g-form__block`}>
                  <MultiLangAnswerLabels
                    sortedButtonLabels={sortedButtonLabels}
                    response={response}
                    languageButton={languageButton}
                    handleUpdateButtonLabel={this.handleUpdateButtonLabel}
                    handleDeleteButtonLabel={this.handleDeleteButtonLabel}
                  />
                </div>
              </div>
            </>
          )}
          <div className="ModalResponseDetails__divider" />
          {shouldShowToggles && (
            <>
              <div className={`${BLOCK_NAME}__row__toggle g-form__block`}>
                <CheckboxPanel
                  label="Reviewable"
                  checked={response.get("reviewable")}
                  handleToggle={this.handleUpdateReviewable}
                  showTooltip
                  tooltipMessage={reviewableTooltipMessage}
                  tooltipImgPath={tooltipReviewableImgPath}
                  tooltipImgAltText={reviewableTooltipMessage}
                  maxWidth={208}
                />
                <p className="ModalResponseDetails__toggle-description">
                  Allow the chatter to review the usefulness of this answer.
                </p>
              </div>
              <hr className="ModalResponseDetails__thin" />
              <div className="ModalResponseDetails__row__toggle g-form__block">
                <CheckboxPanel
                  label={suggestionsTitle}
                  checked={response.get("clarifications")}
                  handleToggle={this.handleUpdateClarifications}
                  tooltipMessage="Include this answer as a clarification or prediction"
                />
                <div>
                  <p className="ModalResponseDetails__toggle-description">
                    {suggestionCopy}
                  </p>
                  {hasPredictiveSuggestions &&
                    (
                      <p className="ModalResponseDetails__toggle-description">
                        Learn more about
                        <a
                          className="ModalResponseDetails__link"
                          href="https://adasupporthelp.zendesk.com/hc/en-us/articles/360017969974--Not-Understood-and-Clarifications"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {" "}Clarifications{" "}
                        </a>
                        and
                        <a
                          className="ModalResponseDetails__link"
                          href="https://adasupporthelp.zendesk.com/hc/en-us/articles/360021144374-Predictive-Suggestions"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {" "}Suggestions{" "}
                        </a>
                      </p>
                    )}
                  {!hasPredictiveSuggestions &&
                    (
                      <p className="ModalResponseDetails__toggle-description">
                        Learn more about
                        <a
                          className="ModalResponseDetails__link"
                          href="https://adasupporthelp.zendesk.com/hc/en-us/articles/360017969974--Not-Understood-and-Clarifications"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {" "}Clarifications{" "}
                        </a>
                      </p>
                    )}
                </div>
              </div>
            </>
          )}
          {shouldShowToggles && (<div className="ModalResponseDetails__divider" />)}
          <div className="ModalResponseDetails__tag-container g-form__block">
            <label
              className="g-input__label"
              htmlFor={`${BLOCK_NAME}__tag-container__search-container__search`}
            >
              Tags
            </label>
            <div className={`${BLOCK_NAME}__tag-container__tags`}>
              {this.renderResponseTags()}
            </div>
            <div className={`${BLOCK_NAME}__tag-container__search-container`}>
              <InputSearch
                customClassName={`${BLOCK_NAME}__tag-container__search-container__search`}
                idForLabel={`${BLOCK_NAME}__tag-container__search-container__search`}
                value={query}
                onChange={(event) => {
                  this.props.setState("MODAL_RESPONSE_DETAILS", {
                    query: event.target.value
                  });
                }}
                onKeyPress={event => event.key === "Enter" && this.addTag(event.target.value)}
                placeholder="Search to add tags"
                hideChevron
              />
              <Button
                onClick={() => this.addTag(query)}
                customClassName={`${BLOCK_NAME}__tag-container__search-container__button`}
                title="Train a New Expression"
                icon="CircleAddAlternate"
                fillColor={
                  query.length ? colors.colorUIPrimary : colors.colorGrey4
                }
                clear
              />
            </div>
            {allTagsWrapper}
          </div>
        </div>
        <div className={`Modal__bottom ${BLOCK_NAME}__bottom`}>
          <Button
            onClick={this.handleSaveResponse}
            title="Save Answer"
            text="Save"
            icon="Cloud"
            isLoading={response.isPublishing}
            disabled={!response.isDirty || !response.isValid}
          />
        </div>
      </div>
    );
  }
}

ModalResponseDetails.propTypes = {
  response: PropTypes.instanceOf(ResponseRecord).isRequired,
  onSave: PropTypes.func,

  client: PropTypes.instanceOf(Client).isRequired,
  languages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)),
  fetchLanguages: PropTypes.func.isRequired,
  saveResponse: PropTypes.func.isRequired,
  addButtonLabelLanguage: PropTypes.func.isRequired
};

ModalResponseDetails.defaultProps = {
  languages: null,
  onSave: null
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapStateToProps(state) {
  const { tags, modalResponseDetails } = state;

  return {
    client: getResource(state, "client"),
    languages: getResources(state, "language"),
    tags,
    modalResponseDetails
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object}
 */
function mapDispatch(dispatch) {
  return {
    fetchLanguages: () => dispatch(fetchMany("language")),
    saveResponse: (...args) => dispatch(saveResponseAction(...args)),
    ...bindActionCreators(
      {
        createAlert,
        createTag
      },
      dispatch
    )
  };
}

const Connector = connect(
  mapStateToProps,
  mapDispatch
)(ModalResponseDetails);

Connector.propTypes = {
  response: PropTypes.instanceOf(ResponseRecord).isRequired,
  onSave: PropTypes.func
};

export default Connector;
